<template>
  <div>
    <h3 class="display-4" @click="enableDebug($event)">Manage Employees</h3>

    <o-list-toolbar
      :filters-list="filter_list"
      :search-fields-list="search_field_list"
      :selected-filter="selected_filter"
      :list-options="list_options"
      @reset_table="reset_table"
      @options_changed="update_table($event)"
      @filter_changed="update_filter($event)"
      :schema="schema"
      :query-select-options="options"
      :new-to="{ name: 'employee.create', params: { id: 0 } }"
      new-button-text="New Employee"
    >
      <template slot="middle">
        <o-list-multi-actions
          :schema="schema"
          :selection="selected"
          @delete="doDeleteItems"
          @merge="doMergeItems"
          enable-delete
          enable-merge
        />
      </template>
    </o-list-toolbar>

    <b-table
      striped
      bordered
      :fields="fields"
      :items="items"
      head-variant="light"
      class="o-list"
      thead-class="o-gradient-header"
      @row-selected="onRowSelected"
      :selectable="selected.selectable"
      :key="selected.selectable"
      no-local-sorting
      @sort-changed="change_sort"
      :sort-by="list_options.sort"
      :sort-desc="list_options.order === 'desc'"
    >
      <template v-slot:cell(_selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true">&check;</span>
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
          <span class="sr-only">Not selected</span>
        </template>
      </template>
      <template
        slot="top-row"
        slot-scope="{ columns, fields }"
        v-if="filterRow"
      >
        <td :key="field.key" class="p-0" v-for="field in fields">
          <span v-if="field.key === 'code'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.code"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'depot'">
            <o-form-select
              v-model="list_options.qbe.depot"
              :options="optionCodeDepotItems"
              text-field="description"
              value-field="id"
              sort-field="description"
              blank-option-text=""
              :loading="optionCodeDepotWorking"
              @change="filter_changed"
              name="depot"
            >
              <template v-slot:first>
                <option :value="null"></option>
              </template>
            </o-form-select>
          </span>
          <span v-if="field.key === 'email_address'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.email_address"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'employee_type'">
            <o-form-select
              v-model="list_options.qbe.employee_type"
              :options="optionCodeEmployeeTypeItems"
              text-field="description"
              value-field="id"
              sort-field="description"
              blank-option-text=""
              :loading="optionCodeEmployeeTypeWorking"
              @change="filter_changed"
              name="employee_type"
            >
              <template v-slot:first>
                <option :value="null"></option>
              </template>
            </o-form-select>
          </span>
          <span v-if="field.key === 'category'">
            <o-form-select
              v-model="list_options.qbe.category"
              :options="optionCodeEmployeeCategoryItems"
              text-field="description"
              value-field="id"
              sort-field="description"
              blank-option-text=""
              :loading="optionCodeEmployeeCategoryWorking"
              @change="filter_changed"
              name="category"
            >
              <template v-slot:first>
                <option :value="null"></option>
              </template>
            </o-form-select>
          </span>
          <span v-if="field.key === 'first_name'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.first_name"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'last_name'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.last_name"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'login'">
            <o-form-select
              v-model="list_options.qbe.login"
              :options="optionUserItems"
              text-field="username"
              value-field="id"
              sort-field="username"
              blank-option-text=""
              :loading="optionUserWorking"
              @change="filter_changed"
              name="login"
            >
              <template v-slot:first>
                <option :value="null"></option>
              </template>
            </o-form-select>
          </span>
        </td>
      </template>
      <template #cell(category)="{ index, item }">
        <o-link-cell
          :to="{ name: 'employee.edit', params: { id: item.id } }"
          :when="first_column_key === 'category'"
        >
          <lookup-display
            :list="optionCodeEmployeeCategoryItemsById"
            :value="item.category"
            display-field="description"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>
      <template slot="cell(code)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'employee.edit', params: { id: item.id } }"
          :when="first_column_key === 'code'"
        >
          <default-display :value="item.code"></default-display>
        </o-link-cell>
      </template>
      <template slot="cell(depot)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'employee.edit', params: { id: item.id } }"
          :when="first_column_key === 'depot'"
        >
          <lookup-display
            :list="optionCodeDepotItemsById"
            :value="item.depot"
            display-field="description"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>
      <template slot="cell(email_address)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'employee.edit', params: { id: item.id } }"
          :when="first_column_key === 'email_address'"
        >
          {{ item.email_address }}
          <a :href="'mailto:' + item.email_address" target="email"
            ><email-icon
          /></a>
        </o-link-cell>
      </template>
      <template slot="cell(employee_type)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'employee.edit', params: { id: item.id } }"
          :when="first_column_key === 'employee_type'"
        >
          <lookup-display
            :list="optionCodeEmployeeTypeItemsById"
            :value="item.employee_type"
            display-field="description"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>
      <template slot="cell(first_name)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'employee.edit', params: { id: item.id } }"
          :when="first_column_key === 'first_name'"
        >
          <default-display :value="item.first_name"></default-display>
        </o-link-cell>
      </template>
      <template slot="cell(last_name)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'employee.edit', params: { id: item.id } }"
          :when="first_column_key === 'last_name'"
        >
          <default-display :value="item.last_name"></default-display>
        </o-link-cell>
      </template>
      <template slot="cell(login)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'employee.edit', params: { id: item.id } }"
          :when="first_column_key === 'login'"
        >
          <lookup-display
            :list="optionUserItemsById"
            :value="item.login"
            display-field="username"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>

      <template slot="empty">
        <p>
          <emoticon-sad-icon large></emoticon-sad-icon>
        </p>
        No Data Found. Please
        <router-link :to="{ name: 'employee.create' }"
          >create a new item</router-link
        >
      </template>
    </b-table>

    <o-list-paging
      :limit="list_options.limit"
      :page-options="rows_per_page_items"
      :offset="list_options.offset"
      :total-items="total_items"
      @change-limit="change_limit"
      @change-page="change_page"
    />
  </div>
</template>

<style></style>

<script>
import {
  CreateCodeTableFetchMixin,
  CreateGroupPermissionMixin,
  CreateListPageMixin,
  MultiActionsMixin,
} from "../../../vue-ocom/mixins";
import FetchAndUseCodeModMixin from "@/vue-ocom/mixins/FetchAndUseCodeModMixin";
import {
  fieldToTableColumn,
  GetSearchFields,
} from "../../../vue-ocom/schema/schema_utils";
import { cleanUpModel } from "../../../vue-ocom/schema/schema_vuetify";
import schema from "../../schema/index";

import OListToolbar from "../../../vue-ocom/components/list/OListToolbar";
import OListPaging from "../../../vue-ocom/components/list/OListPaging";
import DefaultDisplay from "../../../vue-ocom/components/display/DefaultDisplay";
import DateDisplay from "../../../vue-ocom/components/display/DateDisplay";
import NumberDisplay from "../../../vue-ocom/components/display/NumberDisplay";
import BooleanDisplay from "../../../vue-ocom/components/display/BooleanDisplay";
import LookupDisplay from "../../../vue-ocom/components/display/LookupDisplay";
import EmoticonSadIcon from "vue-material-design-icons/EmoticonSad";
import EmailIcon from "vue-material-design-icons/Email";
import LinkIcon from "vue-material-design-icons/Link";

export default {
  components: {
    EmoticonSadIcon,
    EmailIcon,
    LinkIcon,
    OListPaging,
    OListToolbar,
    DateDisplay,
    DefaultDisplay,
    BooleanDisplay,
    NumberDisplay,
    LookupDisplay,
  },
  mixins: [
    CreateListPageMixin("Employee", "employee"),
    MultiActionsMixin,
    CreateGroupPermissionMixin("Employee"),
    FetchAndUseCodeModMixin([
      "User",
      "CodeEmployeeType",
      "CodeDepot",
      "CodeEmployeeCategory",
    ]),
  ],
  data: function () {
    return {
      schemaModel: cleanUpModel(
        schema.Employee,
        {},
      ) /*Use this.schema instead */,

      search_field_list: GetSearchFields(schema.Employee),

      filterRow: true /* Set to False to disable filter row */,
      filter_list: [
        { name: "Active", code: "active", options: { offset: 0, sort: "id" } },
        {
          name: "Non Active",
          code: "inactive",
          options: { offset: 0, sort: "id" },
        },
        { name: "All", code: "all", options: { offset: 0, sort: "id" } },
      ],

      user_column_selection:
        null /* If user can customise the columns set them here */,
      column_override_params: {
        /* Put any column overrides here key by fieldname */
      },
    };
  },
  computed: {
    options() {
      return {
        User: {
          options: this.optionUserItems,
          textField: "username",
          valueField: "id",
        },
        CodeEmployeeType: {
          options: this.optionCodeEmployeeTypeItems,
          textField: "description",
          valueField: "id",
        },
        CodeDepot: {
          options: this.optionCodeDepotItems,
          textField: "description",
          valueField: "id",
        },
        CodeEmployeeCategory: {
          options: this.optionCodeEmployeeCategoryItems,
          textField: "description",
          valueField: "id",
        },
      };
    },
    fields() {
      const vm = this;
      const selectedColumns = this.user_column_selection || [
        "first_name",
        "last_name",
        "code",
        "email_address",
        "employee_type",
        "depot",
        "category",
      ];
      const fields = selectedColumns.map(function (item) {
        return fieldToTableColumn(
          vm.schema,
          item,
          vm.column_override_params[item],
        );
      });

      return this.addSelectionColums(
        this.filterTableColumns(this.schema, fields),
      );
    },
    first_column_key() {
      if (this.fields) {
        return this.fields[0].key;
      } else {
        return null;
      }
    },
  },
};
</script>
